import { useState, useEffect } from "react";
import { Bell, BellOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { requestNotificationPermission, onForegroundMessage, initializeMessaging } from "@/lib/firebase";

export const PushNotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [isLoading, setIsLoading] = useState(false);

  // Setup foreground message handler when permission is granted
  useEffect(() => {
    if (permission === "granted") {
      console.log('[PushNotification] Permission granted, setting up foreground handler');
      onForegroundMessage((payload) => {
        console.log('[PushNotification] Received foreground message:', payload);
        toast(payload.notification?.title || "Nova notificação", {
          description: payload.notification?.body,
        });
      });
    }
  }, [permission]);

  useEffect(() => {
    // Check if push notifications are supported
    const supported = "Notification" in window && "serviceWorker" in navigator;
    setIsSupported(supported);

    if (supported) {
      const currentPermission = Notification.permission;
      setPermission(currentPermission);
      console.log('[PushNotification] Current permission:', currentPermission);

      // Show prompt if permission hasn't been asked yet
      if (currentPermission === "default") {
        const dismissed = localStorage.getItem("push-prompt-dismissed");
        if (!dismissed) {
          const timer = setTimeout(() => {
            setShowPrompt(true);
          }, 10000);
          return () => clearTimeout(timer);
        }
      }
    }
  }, []);

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem("push-prompt-dismissed", Date.now().toString());
  };

  const registerToken = async (token: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      if (session?.access_token) {
        headers["Authorization"] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(
        `https://rrybrsyrtmybarwussul.supabase.co/functions/v1/register-push-token`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            token,
            deviceInfo: {
              userAgent: navigator.userAgent,
              platform: navigator.platform,
              language: navigator.language,
            },
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to register token");
      }

      console.log("Push token registered successfully");
    } catch (error) {
      console.error("Error registering token:", error);
      throw error;
    }
  };

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    
    try {
      // Request permission and get FCM token
      const token = await requestNotificationPermission();
      
      if (token) {
        // Register the token with our backend
        await registerToken(token);
        setPermission("granted");
        toast.success("Notificações ativadas com sucesso!");
        setShowPrompt(false);
      } else {
        const currentPermission = Notification.permission;
        setPermission(currentPermission);
        if (currentPermission === "denied") {
          toast.error("Permissão negada. Você pode ativar nas configurações do navegador.");
        }
      }
    } catch (error) {
      console.error("Error enabling notifications:", error);
      toast.error("Erro ao ativar notificações. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSupported || !showPrompt || permission !== "default") {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-40 md:left-auto md:right-4 md:max-w-sm animate-in slide-in-from-bottom-4">
      <div className="bg-card border border-border rounded-lg shadow-lg p-4">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          aria-label="Fechar"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Bell className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-sm font-medium mb-1">Ativar Notificações</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Receba avisos sobre giras, eventos e novidades do terreiro
            </p>

            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleEnableNotifications}
                disabled={isLoading}
                className="flex-1"
              >
                <Bell className="w-4 h-4 mr-1" />
                {isLoading ? "Ativando..." : "Ativar"}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDismiss}>
                Agora não
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Component to show notification status in profile/settings
export const NotificationStatus = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const supported = "Notification" in window && "serviceWorker" in navigator;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  const handleToggle = async () => {
    if (permission === "granted") {
      toast.info("Para desativar, acesse as configurações do seu navegador");
      return;
    }

    setIsLoading(true);
    try {
      const token = await requestNotificationPermission();
      if (token) {
        setPermission("granted");
        toast.success("Notificações ativadas!");
      }
    } catch (error) {
      toast.error("Erro ao configurar notificações");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <BellOff className="w-4 h-4" />
        <span className="text-sm">Notificações não suportadas neste navegador</span>
      </div>
    );
  }

  return (
    <Button
      variant={permission === "granted" ? "outline" : "default"}
      size="sm"
      onClick={handleToggle}
      disabled={isLoading}
    >
      {permission === "granted" ? (
        <>
          <Bell className="w-4 h-4 mr-2" />
          Notificações Ativas
        </>
      ) : (
        <>
          <BellOff className="w-4 h-4 mr-2" />
          {isLoading ? "Ativando..." : "Ativar Notificações"}
        </>
      )}
    </Button>
  );
};
